import { useState } from "react";
import { ArrowRight, ChevronDown, Minus, PencilLine, Plus, Undo2 } from "lucide-react";
import { Button } from "../../components/Button";
import { formatMoney, formatWholeMoney, toDecimal } from "../../lib/money";
import { CHANGE_KIND_LABEL } from "../../api/types";
import type {
  ChangeKind,
  DiffField,
  DiffLine,
  DiffSection,
  DiffSnapshotRow,
  DiffTotal,
  Money,
  QuoteDiff,
} from "../../api/types";

// 差異的呈現方式是業主選定的**變更清單**:只列真的改了的東西,最上面先講
// 總金額差多少,每一行可展開看該列的前後對照。不做並排對照整張單 ——
// 一張七大類四五十列的單子並排起來,人要自己找哪裡不一樣,等於沒做。
//
// 這裡不判斷「有沒有變」:後端的 GET /diff 只回報有變化的東西,UNCHANGED 一律
// 不出現在回應裡。前端照著畫就好,不自己補、也不自己濾 —— 有得濾就有機會濾錯。
//
// 欄位的中文名一律用後端給的 `label`,前端不另建一份對照表:兩邊各存一份,
// 遲早會有一邊改了另一邊沒改,而畫面上看起來還是好好的。

/**
 * 增減值的顯示:千分位 + 正負號,0 就是 0 不加號。
 * 一律走 decimal.js,不用 parseFloat 判斷正負。
 */
export function signedMoney(value: Money | null | undefined): string {
  const text = formatMoney(value);
  if (toDecimal(value).isZero()) return text;
  return text.startsWith("-") ? text : `+${text}`;
}

/**
 * 要用千分位重新排版的欄位,刻意逐一列出。
 * 「看起來像數字就格式化」會把只打了數字的摘要或備註也一起改掉。
 */
const MONEY_FIELDS = new Set(["unit_price", "lump_sum_amount"]);

/** 欄位值的顯示。空字串要講出來,否則「改成空白」在畫面上看起來像什麼都沒發生。 */
function fieldValue(field: DiffField, side: "old" | "new"): string {
  const raw = side === "old" ? field.old : field.new;
  if (raw === "") return "(空白)";
  return MONEY_FIELDS.has(field.name) ? formatMoney(raw) : raw;
}

/** 快照裡的一格。null / undefined 一律當成空字串,不讓 "null" 印到畫面上。 */
function cell(row: DiffSnapshotRow, key: string): string {
  const value = row[key];
  return value === null || value === undefined ? "" : String(value);
}

/**
 * 一列的計量式,例「1 式 × 46,000」或「16 人 × 1 天 × 3,000」。
 * 有填人數或天數就是人力版面的算法,與計價引擎看的是同一組欄位。
 */
function measure(row: DiffSnapshotRow): string {
  const price = formatWholeMoney(cell(row, "unit_price"));
  const headcount = cell(row, "headcount");
  const days = cell(row, "days");
  if (headcount !== "" || days !== "") {
    return [`${headcount || "0"} 人`, `${days || "0"} 天`, price].join(" × ");
  }
  const qty = cell(row, "qty") || "0";
  const unit = cell(row, "unit");
  return [unit === "" ? qty : `${qty} ${unit}`, price].join(" × ");
}

const BADGE_ICON: Record<ChangeKind, typeof Plus> = {
  ADDED: Plus,
  REMOVED: Minus,
  CHANGED: PencilLine,
  UNCHANGED: PencilLine,
};

const BADGE_CLASS: Record<ChangeKind, string> = {
  ADDED: "border-[var(--ok)] text-[var(--ok)]",
  REMOVED: "border-[var(--danger)] text-[var(--danger)]",
  CHANGED: "border-[var(--accent)] text-[var(--accent-hover)]",
  UNCHANGED: "border-[var(--border)] text-[var(--text-3)]",
};

/** 變更標記。顏色之外一律帶文字,色盲使用者與黑白列印都讀得出來。 */
function ChangeBadge({ change }: { change: ChangeKind }) {
  const Icon = BADGE_ICON[change];
  return (
    <span
      className={
        "inline-flex shrink-0 items-center gap-1 rounded-[var(--radius)] border px-1.5 py-0.5 " +
        `text-xs font-medium ${BADGE_CLASS[change]}`
      }
    >
      <Icon aria-hidden="true" size={12} />
      {CHANGE_KIND_LABEL[change]}
    </span>
  );
}

/** 一個金額欄的前後與增減。 */
function TotalRow({ label, total, strong = false }: { label: string; total: DiffTotal; strong?: boolean }) {
  return (
    <div className={`flex flex-wrap items-baseline gap-2 ${strong ? "text-base font-semibold" : "text-sm"}`}>
      <span className="w-16 shrink-0 text-[var(--text-2)]">{label}</span>
      <span className="tabular text-[var(--text-3)]">{formatMoney(total.old)}</span>
      <ArrowRight aria-hidden="true" size={14} className="shrink-0 text-[var(--text-3)]" />
      <span className="tabular text-[var(--text)]">{formatMoney(total.new)}</span>
      <span className="tabular ml-auto text-[var(--text-2)]">{signedMoney(total.delta)}</span>
    </div>
  );
}

const ZERO_TOTAL: DiffTotal = { old: "0.00", new: "0.00", delta: "0.00" };

/** 改過的欄位清單:中文欄位名 + 前後值。 */
function FieldList({ fields }: { fields: DiffField[] }) {
  if (fields.length === 0) return null;
  return (
    <ul className="m-0 mt-1 flex list-none flex-col gap-0.5 p-0 pl-6">
      {fields.map((field) => (
        <li key={field.name} className="flex flex-wrap items-baseline gap-2 text-sm">
          <span className="text-[var(--text-2)]">{field.label}</span>
          <span className="tabular text-[var(--text-3)]">{fieldValue(field, "old")}</span>
          <ArrowRight aria-hidden="true" size={12} className="shrink-0 text-[var(--text-3)]" />
          <span className="tabular text-[var(--text)]">{fieldValue(field, "new")}</span>
        </li>
      ))}
    </ul>
  );
}

/** 展開後的一側(改前 / 改後)。那一版沒有這一列時要講出來,不要給一欄空白。 */
function LineSide({ label, row }: { label: string; row: DiffSnapshotRow | null }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="w-10 shrink-0 font-medium text-[var(--text-2)]">{label}</span>
      {row === null ? (
        <span className="text-[var(--text-3)]">這一版沒有這一列</span>
      ) : (
        <span className="min-w-0 flex-1">
          <span className="block whitespace-pre-wrap text-[var(--text)]">{cell(row, "name") || "(未命名明細)"}</span>
          {cell(row, "spec") !== "" ? (
            <span className="block whitespace-pre-wrap text-[var(--text-2)]">{cell(row, "spec")}</span>
          ) : null}
          {cell(row, "kind") === "ITEM" ? (
            <span className="tabular block text-[var(--text-2)]">
              {measure(row)} = {formatMoney(cell(row, "amount"))}
            </span>
          ) : null}
          {cell(row, "remark") !== "" ? (
            <span className="block whitespace-pre-wrap text-[var(--text-3)]">{cell(row, "remark")}</span>
          ) : null}
        </span>
      )}
    </div>
  );
}

interface LineItemProps {
  line: DiffLine;
  onRestore: () => void;
  restoreDisabledReason?: string;
}

function LineItem({ line, onRestore, restoreDisabledReason }: LineItemProps) {
  const [expanded, setExpanded] = useState(false);
  const name = line.name || "(未命名明細)";
  // 新增與刪除的列沒有「改了哪幾格」可講,直接把整列的計量式印在同一行,
  // 使用者不必展開就知道加了什麼、刪了什麼。
  const inline = line.change === "ADDED" ? line.new : line.change === "REMOVED" ? line.old : null;

  return (
    <li className="border-t border-[var(--border)] px-3 py-2 first:border-t-0">
      <div className="flex flex-wrap items-center gap-2">
        <ChangeBadge change={line.change} />
        <span className="tabular shrink-0 text-xs text-[var(--text-3)]">第 {line.position} 列</span>
        <span className="min-w-0 whitespace-pre-wrap text-sm text-[var(--text)]">{name}</span>
        {inline !== null && cell(inline, "kind") === "ITEM" ? (
          <span className="tabular text-xs text-[var(--text-2)]">{measure(inline)}</span>
        ) : null}
        <span className="tabular ml-auto text-sm text-[var(--text-2)]">{signedMoney(line.amount_delta)}</span>
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={`${expanded ? "收合" : "展開"}「${name}」的前後對照`}
          onClick={() => setExpanded((prev) => !prev)}
          className="rounded-[var(--radius)] p-1 text-[var(--text-3)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
        >
          <ChevronDown aria-hidden="true" size={16} className={expanded ? "rotate-180" : undefined} />
        </button>
        {line.uid ? (
          <Button
            size="sm"
            icon={<Undo2 aria-hidden="true" size={14} />}
            aria-label={`復原這一列:${name}`}
            disabled={restoreDisabledReason !== undefined}
            title={restoreDisabledReason}
            onClick={onRestore}
          >
            復原這一列
          </Button>
        ) : null}
      </div>

      <FieldList fields={line.fields} />

      {expanded ? (
        <div
          role="group"
          aria-label={`「${name}」的前後對照`}
          className="mt-2 flex flex-col gap-2 rounded-[var(--radius)] bg-[var(--surface-2)] px-3 py-2"
        >
          <LineSide label="改前" row={line.old} />
          <LineSide label="改後" row={line.new} />
        </div>
      ) : null}
    </li>
  );
}

interface SectionBlockProps {
  section: DiffSection;
  onRestoreSection: () => void;
  onRestoreLine: (line: DiffLine) => void;
  restoreDisabledReason?: string;
}

function SectionBlock({
  section,
  onRestoreSection,
  onRestoreLine,
  restoreDisabledReason,
}: SectionBlockProps) {
  const title = section.title || "(未命名分類)";
  return (
    <section
      aria-label={`分類:${title}`}
      className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]"
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--border)] px-3 py-2">
        <ChangeBadge change={section.change} />
        {section.code !== "" ? (
          <span className="shrink-0 text-sm text-[var(--text-3)]">{section.code}</span>
        ) : null}
        <span className="min-w-0 text-sm font-medium text-[var(--text)]">{title}</span>
        <span className="ml-auto text-xs text-[var(--text-3)]">小計</span>
        <span className="tabular text-sm text-[var(--text-2)]">{signedMoney(section.subtotal_delta)}</span>
        {section.uid ? (
          <Button
            size="sm"
            icon={<Undo2 aria-hidden="true" size={14} />}
            aria-label={`復原這個分類:${title}`}
            disabled={restoreDisabledReason !== undefined}
            title={restoreDisabledReason}
            onClick={onRestoreSection}
          >
            復原這個分類
          </Button>
        ) : null}
      </div>

      <FieldList fields={section.fields} />

      {section.lines_reordered ? (
        <p className="m-0 px-3 py-1.5 text-xs text-[var(--text-3)]">明細的順序調整過。</p>
      ) : null}

      {section.lines.length > 0 ? (
        <ul aria-label={`「${title}」的變更明細`} className="m-0 flex list-none flex-col p-0">
          {section.lines.map((line) => (
            <LineItem
              key={line.uid ?? `${line.change}-${line.position}`}
              line={line}
              onRestore={() => onRestoreLine(line)}
              restoreDisabledReason={restoreDisabledReason}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export interface DiffViewProps {
  diff: QuoteDiff | null;
  loading: boolean;
  error: string | null;
  onRestoreSection: (section: DiffSection) => void;
  onRestoreLine: (section: DiffSection, line: DiffLine) => void;
  /**
   * 有值就表示「現在不能復原」,兩種粒度的復原鈕一律 disabled 並把這一句掛上
   * `title`。**畫出來但按不動,不藏起來** —— 藏起來只會讓人以為差異清單壞了。
   *
   * 目前唯一的來源是「這張單已封存」(規格 §4.1)。做成一句話而不是布林值,
   * 是因為按不動的東西一定要說得出為什麼。
   */
  restoreDisabledReason?: string;
}

export function DiffView({
  diff,
  loading,
  error,
  onRestoreSection,
  onRestoreLine,
  restoreDisabledReason,
}: DiffViewProps) {
  if (error !== null) {
    return (
      <p role="alert" className="m-0 px-3 py-3 text-sm text-[var(--danger)]">
        {error}
      </p>
    );
  }
  if (loading) return <p className="m-0 px-3 py-3 text-sm text-[var(--text-3)]">載入中…</p>;
  if (diff === null) return null;

  // 兩版之間一個變化都沒有時要講出來。給一片空白的話,使用者無從分辨是
  // 「真的沒改」還是「畫面壞了」。
  const identical =
    diff.sections.length === 0 && diff.header_fields.length === 0 && !diff.sections_reordered;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <h3 className="m-0 text-sm font-semibold text-[var(--text)]">
        版本 {diff.from_version} → 版本 {diff.to_version}
      </h3>

      {identical ? (
        <p className="m-0 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-3 py-3 text-sm text-[var(--text-2)]">
          這兩個版本的內容相同
        </p>
      ) : (
        <>
          <section
            aria-label="總計變化"
            className="flex flex-col gap-1 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-3 py-2"
          >
            <TotalRow label="總計" total={diff.totals.total ?? ZERO_TOTAL} strong />
            <TotalRow label="小計" total={diff.totals.subtotal ?? ZERO_TOTAL} />
            <TotalRow label="營業稅" total={diff.totals.tax_amount ?? ZERO_TOTAL} />
          </section>

          {diff.sections_reordered ? (
            <p className="m-0 text-xs text-[var(--text-3)]">分類的順序調整過。</p>
          ) : null}

          {diff.header_fields.length > 0 ? (
            <section
              aria-label="報價單資料"
              className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-3 py-2"
            >
              <span className="text-sm font-medium text-[var(--text)]">報價單資料</span>
              <FieldList fields={diff.header_fields} />
            </section>
          ) : null}

          {diff.sections.map((section, index) => (
            <SectionBlock
              key={section.uid ?? `section-${index}`}
              section={section}
              onRestoreSection={() => onRestoreSection(section)}
              onRestoreLine={(line) => onRestoreLine(section, line)}
              restoreDisabledReason={restoreDisabledReason}
            />
          ))}
        </>
      )}
    </div>
  );
}
